import React, { useState, useEffect } from 'react';
import { Clock, Calendar, Flame, HelpCircle as CircleHelp, Thermometer } from 'lucide-react';
import { FERMENTATION_PROCESSES } from '../../../types/baking';

export function SessionTimingCalculator() {
  const [fermentationType, setFermentationType] = useState('direct');
  const [targetBakeTime, setTargetBakeTime] = useState('');
  const [customBulkTime, setCustomBulkTime] = useState<number>(24);
  const [customProofTime, setCustomProofTime] = useState<number>(24);
  const [milestones, setMilestones] = useState<{
    kneading: Date | null;
    shaping: Date | null;
    baking: Date | null;
  }>({
    kneading: null,
    shaping: null,
    baking: null
  });

  useEffect(() => {
    if (!targetBakeTime) {
      setMilestones({
        kneading: null,
        shaping: null,
        baking: null
      });
      return;
    }

    const process = FERMENTATION_PROCESSES.find(p => p.id === fermentationType);
    if (!process) return;

    const bakeTime = new Date(targetBakeTime);
    
    // Calculate total time based on process and custom durations
    let totalTime = process.totalTime;
    let bulkTime = process.bulkTime;
    let proofTime = process.proofTime;

    // For cold bulk fermentation, allow custom bulk time
    if (fermentationType === 'cold-bulk' && customBulkTime !== '') {
      totalTime = Number(customBulkTime) + process.proofTime;
      bulkTime = Number(customBulkTime);
    }

    // For cold retard, allow custom proof time
    if (fermentationType === 'pre-proofed' && customProofTime !== '') {
      totalTime = process.bulkTime + Number(customProofTime);
      proofTime = Number(customProofTime);
    }

    // Calculate kneading time (start of the process)
    const kneadingTime = new Date(bakeTime);
    kneadingTime.setHours(kneadingTime.getHours() - totalTime);

    // Calculate shaping time (after bulk fermentation)
    const shapingTime = new Date(bakeTime);
    shapingTime.setHours(shapingTime.getHours() - proofTime);

    setMilestones({
      kneading: kneadingTime,
      shaping: shapingTime,
      baking: bakeTime
    });
  }, [targetBakeTime, fermentationType, customBulkTime, customProofTime]);

  const formatDateTime = (date: Date | null) => {
    if (!date) return '';
    return date.toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6 text-white/90">
      <div className="bg-gray-600/60 p-6">
        <div className="space-y-6">
          <div className="flex items-start gap-4 p-4 bg-amber-500/10 backdrop-blur-sm rounded-xl border border-amber-500/20">
            <div className="flex-shrink-0 mt-1">
              <Clock className="w-6 h-6 text-amber-400" />
            </div>
            <div>
              <h3 className="text-lg font-medium text-amber-400 mb-2">About this Calculator</h3>
              <p className="text-sm text-white/90">
                Plan your sourdough baking process with this simulator, which helps you select the best fermentation method based on your schedule. Whether you opt for direct fermentation, slow room-temperature fermentation, cold fermentation, or cold retard, this tool helps you manage your timing for the perfect bake.
              </p>
            </div>
          </div>

          {/* Process Selection */}
          <div className="space-y-4">
            <label className="block text-sm font-medium text-white/90">
              1. Select a fermentation Process
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {FERMENTATION_PROCESSES.map(process => (
                <button
                  key={process.id}
                  onClick={() => {
                    setFermentationType(process.id);
                    if (process.id === 'cold-bulk') {
                      setCustomBulkTime(24);
                    } else if (process.id === 'pre-proofed') {
                      setCustomProofTime(24);
                    }
                  }}
                  className={`p-4 border rounded-xl flex flex-col gap-2 transition-colors ${
                    fermentationType === process.id
                      ? 'border-amber-400/50 bg-gradient-to-b from-amber-400/20 to-amber-500/10'
                      : 'border-white/20 bg-white/5 hover:bg-white/10'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-white">{process.name}</span>
                    <span className="text-sm text-amber-400">{process.totalTime}h</span>
                  </div>
                  <p className="text-sm text-white/70">{process.description}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Custom Duration Inputs */}
          {fermentationType === 'cold-bulk' && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-white/90">
                Cold Bulk Fermentation Duration ({customBulkTime} hours)
              </label>
              <input
                type="range"
                min="8"
                max="120"
                step="1"
                value={customBulkTime}
                onChange={(e) => setCustomBulkTime(Number(e.target.value))}
                className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-runnable-track]:rounded-lg [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-amber-400 [&::-webkit-slider-thumb]:border [&::-webkit-slider-thumb]:border-white/50 [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-sm [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-amber-400 [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:shadow-sm [&::-moz-range-progress]:bg-amber-400 [&::-moz-range-progress]:rounded-l-lg [&::-moz-range-progress]:h-2"
                style={{
                  background: `linear-gradient(to right, rgb(251 191 36) 0%, rgb(251 191 36) ${(customBulkTime - 8) * 100 / 112}%, rgba(255, 255, 255, 0.1) ${(customBulkTime - 8) * 100 / 112}%, rgba(255, 255, 255, 0.1) 100%)`
                }}
              />
              <div className="flex justify-between text-xs text-white/50 mt-1">
                <span>8h</span>
                <span>120h</span>
              </div>
              <p className="text-sm text-white/70">
                Recommended: 16-24 hours for optimal flavor development
              </p>
            </div>
          )}

          {fermentationType === 'pre-proofed' && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-white/90">
                Cold Proof Duration ({customProofTime} hours)
              </label>
              <input
                type="range"
                min="8"
                max="120"
                step="1"
                value={customProofTime}
                onChange={(e) => setCustomProofTime(Number(e.target.value))}
                className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-runnable-track]:rounded-lg [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-amber-400 [&::-webkit-slider-thumb]:border [&::-webkit-slider-thumb]:border-white/50 [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-sm [&::-webkit-slider-thumb]:-mt-1 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-amber-400 [&::-moz-range-thumb]:border [&::-moz-range-thumb]:border-white/50 [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:shadow-sm [&::-moz-range-progress]:bg-amber-400 [&::-moz-range-progress]:rounded-l-lg [&::-moz-range-progress]:h-2"
                style={{
                  background: `linear-gradient(to right, rgb(251 191 36) 0%, rgb(251 191 36) ${(customProofTime - 8) * 100 / 112}%, rgba(255, 255, 255, 0.1) ${(customProofTime - 8) * 100 / 112}%, rgba(255, 255, 255, 0.1) 100%)`
                }}
              />
              <div className="flex justify-between text-xs text-white/50 mt-1">
                <span>8h</span>
                <span>120h</span>
              </div>
              <p className="text-sm text-white/70">
                Recommended: 12-48 hours for best results
              </p>
            </div>
          )}

          {/* Target Bake Time */}
          <div className="space-y-4">
            <label className="block text-sm font-medium text-white/90">
              2. Select a target Bake Time
            </label>
            <input
              type="datetime-local"
              value={targetBakeTime}
              onChange={(e) => setTargetBakeTime(e.target.value)}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:border-amber-300/50 focus:ring-2 focus:ring-amber-300/20"
            />
          </div>

          {/* Results */}
          {targetBakeTime && (
            <div className="space-y-4">
              <h3 className="font-medium text-sm text-white">Your Process Timeline</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 bg-white/10 rounded-xl backdrop-blur-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="w-5 h-5 text-amber-400" />
                    <h4 className="font-medium text-white">Kneading Time</h4>
                  </div>
                  <p className="text-white/80">{formatDateTime(milestones.kneading)}</p>
                </div>

                <div className="p-4 bg-white/10 rounded-xl backdrop-blur-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="w-5 h-5 text-amber-400" />
                    <h4 className="font-medium text-white">Shaping Time</h4>
                  </div>
                  <p className="text-white/80">{formatDateTime(milestones.shaping)}</p>
                </div>

                <div className="p-4 bg-white/10 rounded-xl backdrop-blur-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <Flame className="w-5 h-5 text-amber-400" />
                    <h4 className="font-medium text-white">Baking Time</h4>
                  </div>
                  <p className="text-white/80">{formatDateTime(milestones.baking)}</p>
                </div>
              </div>

              <div className="p-4 bg-amber-500/10 backdrop-blur-sm rounded-xl mt-6">
                <h4 className="text-amber-400 font-medium mb-2">Process Details</h4>
                <div className="space-y-2 text-sm text-white/80">
                  <p>• Start kneading your dough at the specified kneading time</p>
                  <p>• Bulk fermentation will occur between kneading and shaping</p>
                  <p>• Final proof will occur between shaping and baking</p>
                  <p>• Have your oven preheated 10-15 minutes before baking time</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}